import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import type {Audience} from 'crqlar-react-common/dist/models/audience/audience.model';
import {MixpanelService} from 'crqlar-react-common/dist/services/analytics/mixpanel.service';
import {AudienceService} from 'crqlar-react-common/dist/services/audience/audience.service';
import {
    useEstimatedAudienceSize,
    useEstimatedMarketingAudienceSize
} from 'crqlar-react-common/dist/utils/hooks/audience/useAudience';
import {getErrorMessage} from 'crqlar-react-common/dist/utils/http.util';
import {isEqual} from 'lodash';
import {useSnackbar} from 'notistack';
import {useTranslation} from 'react-i18next';
import {useQuery, useQueryClient} from 'react-query';

import AudienceForm from '../../../../AudienceForm';
import {SingleSelect} from '../../../../../common/newSelect';
import NodeEditorLayout, {NodeEditorState} from '../NodeEditorLayout';
import {NodeBeforeSaveResult, NodeEditorV2Props} from '../types';
import {readAudienceChoice} from './AudienceModePicker';

/** Its own key: nothing else in the app wants every audience of the organisation in one list. */
export const AUTOMATION_AUDIENCES_QUERY_KEY = ['automation-audience-picker'];

/** The marketing module's audiences table, refreshed whenever this step writes an audience. */
const AUDIENCES_TABLE_QUERY_KEY = ['server-table', 'marketing-audiences'];

const AUDIENCE_PAGE_SIZE = 1000;

/** One shared empty audience: a fresh `{}` per render would rebuild the before-save hook forever. */
const NO_AUDIENCE: Audience = {};

interface AudienceOption {
    id: string;
    name: string;
    audienceSize?: number;
    marketingAudienceSize?: number;
    /** A stored id the list no longer holds: still selected, and labelled as gone. */
    unavailable?: boolean;
}

/** What counts as an edit; the rest of an audience is server-owned or written alongside the filter. */
const fingerprint = (audience?: Audience) => ({
    name: audience?.name?.trim() ?? '',
    channels: [...(audience?.syncData ?? [])].map(sync => sync.channel).sort(),
    filter: audience?.filter ?? {},
    query: audience?.query ?? ''
});

/**
 * The Audience step: stores only `{audienceId, audienceName, marketingConsentOnly}` and embeds the marketing
 * AudienceForm. The header (`headerChoice`) picks Use/Create; each side keeps its own draft. Save creates or
 * updates the audience first (`registerBeforeSave`), never an untouched one. Deleting audiences stays in Marketing.
 */
const AudienceEditorV2 = ({
    nodeConfig,
    setNodeConfig,
    registerBeforeSave,
    headerChoice,
    setHeaderChoice,
    readOnly
}: NodeEditorV2Props) => {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const {enqueueSnackbar} = useSnackbar();

    const marketingConsentOnly = !!nodeConfig.marketingConsentOnly;

    // The dialog owns the header choice; `''` (never asked) reads as "saved".
    const choice = readAudienceChoice(headerChoice);

    // Each side keeps its state while the other shows. savedDraft = the form's copy, loaded = the server's.
    const [savedId, setSavedId] = useState<string>(nodeConfig.audienceId || '');
    const [savedName, setSavedName] = useState<string>(nodeConfig.audienceName || '');
    const [savedDraft, setSavedDraft] = useState<Audience | undefined>(undefined);
    const [loaded, setLoaded] = useState<Audience | undefined>(undefined);
    const [newDraft, setNewDraft] = useState<Audience>({});
    /** Which stored id the form already holds, so loading never overwrites what the user typed. */
    const hydratedIdRef = useRef<string | null>(null);

    const {data: audiences = [], isFetching} = useQuery({
        queryKey: AUTOMATION_AUDIENCES_QUERY_KEY,
        queryFn: async () => {
            const collected: Audience[] = [];
            let offset = 0;

            for (;;) {
                const page = await AudienceService.getAllAudiences({
                    archived: false,
                    limit: {
                        offset,
                        limit: AUDIENCE_PAGE_SIZE,
                        sort: [{field: 'name', direction: 'ASC'}]
                    }
                });

                const content = page.content ?? [];
                collected.push(...content);

                if (page.last || content.length < AUDIENCE_PAGE_SIZE) break;
                offset += AUDIENCE_PAGE_SIZE;
            }

            return collected;
        }
    });

    const options = useMemo<AudienceOption[]>(() => {
        const known = audiences
            .filter(audience => !!audience.id)
            .map(audience => ({
                id: audience.id!,
                name: audience.name || t('Unnamed audience'),
                audienceSize: audience.audienceSize,
                marketingAudienceSize: audience.marketingAudienceSize
            }))
            .sort((left, right) => left.name.localeCompare(right.name));

        if (!savedId || known.some(option => option.id === savedId)) return known;

        return [
            ...known,
            {id: savedId, name: savedName || savedId, unavailable: true}
        ];
    }, [audiences, savedId, savedName, t]);

    const selected = options.find(option => option.id === savedId) ?? null;
    // While the list is being fetched, a stored audience is not "gone" — it is just not here yet.
    const missing = !!savedId && !isFetching && !!selected?.unavailable;
    // A stored audience whose record has not arrived yet shows a spinner, not the empty form.
    const hydrating = !!savedId && hydratedIdRef.current !== savedId && !missing;

    useEffect(() => {
        if (!savedId || hydratedIdRef.current === savedId) return;

        const stored = audiences.find(audience => audience.id === savedId);
        if (!stored) return;

        hydratedIdRef.current = savedId;
        setLoaded(stored);
        setSavedDraft(stored);
    }, [savedId, audiences]);

    const writeSelection = (id: string, name: string) => setNodeConfig({
        ...nodeConfig,
        audienceId: id,
        audienceName: name,
        // Legacy nodes carry an inline filter/query; drop it once an audience is chosen, never on open.
        ...(id ? {filter: undefined, query: undefined} : {})
    });

    const handleSelect = (option: AudienceOption | null) => {
        const stored = option ? audiences.find(audience => audience.id === option.id) : undefined;

        hydratedIdRef.current = option?.id ?? '';
        setSavedId(option?.id ?? '');
        setSavedName(option?.name ?? '');
        setLoaded(stored);
        setSavedDraft(stored);
        writeSelection(option?.id ?? '', option?.name ?? '');
    };

    const creating = choice === 'new';
    const draft = creating ? newDraft : savedDraft ?? NO_AUDIENCE;
    // Subscribe to the same filter queries as AudienceForm; stored sizes can predate membership changes.
    const audienceSizeQ = useEstimatedAudienceSize(draft);
    const marketingAudienceSizeQ = useEstimatedMarketingAudienceSize(draft);
    // No baseline = create, a baseline = update.
    const baseline = creating ? undefined : loaded;
    const setDraft = (next: Audience) => (creating ? setNewDraft(next) : setSavedDraft(next));

    const draftName = draft.name?.trim() ?? '';
    // The marketing dialog's rule: a name and a filter; an empty filter object means everyone and is valid.
    const draftComplete = !!draftName && !!draft.filter;
    const draftChanged = !!baseline?.id && !isEqual(fingerprint(draft), fingerprint(baseline));

    const beforeSave = useCallback(async (): Promise<NodeBeforeSaveResult> => {
        if (readOnly) return true;

        const name = draft.name?.trim();
        const hasFilter = !!draft.filter;

        // Nothing to write: a half-done node may be saved. On the Create side it lets go of its old audience.
        if (!baseline?.id && !name && !hasFilter) {
            return creating && !!nodeConfig.audienceId
                ? {config: {...nodeConfig, audienceId: '', audienceName: ''}}
                : true;
        }

        if (!name || !hasFilter) {
            enqueueSnackbar(t('An audience needs a name and at least one rule.'), {variant: 'error'});
            return false;
        }

        // Loaded and untouched: no request, just make sure the node points at it.
        if (baseline?.id && isEqual(fingerprint(draft), fingerprint(baseline))) {
            return {config: {...nodeConfig, audienceId: baseline.id, audienceName: baseline.name ?? ''}};
        }

        try {
            const isCreate = !baseline?.id;
            const response = isCreate
                ? await AudienceService.createAudience(draft)
                : await AudienceService.updateAudience(draft);

            // A create response may have no body; fall back to what was sent, as the marketing dialog does.
            const saved: Audience = {...draft, ...response};

            if (!saved.id) {
                enqueueSnackbar(t('The audience could not be saved.'), {variant: 'error'});
                return false;
            }

            if (isCreate) {
                MixpanelService.getInstance().track('audience created', {
                    name: saved.name,
                    id: saved.id
                });
            }

            // Seed the picker's cache before refetching, or the new id reads as "(no longer available)".
            queryClient.setQueryData<Audience[]>(AUTOMATION_AUDIENCES_QUERY_KEY, current => {
                const list = current ?? [];
                return list.some(entry => entry.id === saved.id)
                    ? list.map(entry => (entry.id === saved.id ? saved : entry))
                    : [...list, saved];
            });
            void queryClient.invalidateQueries(AUTOMATION_AUDIENCES_QUERY_KEY);
            void queryClient.invalidateQueries(AUDIENCES_TABLE_QUERY_KEY);

            // Move to the saved side; matters only if the dialog ever stays open after Save.
            hydratedIdRef.current = saved.id;
            setHeaderChoice?.('saved');
            setSavedId(saved.id);
            setSavedName(saved.name ?? '');
            setLoaded(saved);
            setSavedDraft(saved);
            if (isCreate) setNewDraft({});

            const config = {
                ...nodeConfig,
                audienceId: saved.id,
                audienceName: saved.name ?? '',
                filter: undefined,
                query: undefined
            };
            setNodeConfig(config);
            return {config};
        } catch (error) {
            enqueueSnackbar(getErrorMessage(error), {variant: 'error'});
            return false;
        }
    }, [draft, baseline, creating, nodeConfig, setNodeConfig, setHeaderChoice, queryClient, enqueueSnackbar, readOnly, t]);

    useEffect(() => {
        registerBeforeSave?.(beforeSave);
        return () => registerBeforeSave?.(null);
    }, [registerBeforeSave, beforeSave]);

    const optionCount = (option: AudienceOption) => {
        if (option.unavailable) return '';

        const profiles = option.id === savedId ? audienceSizeQ.data : option.audienceSize;
        const marketing = option.id === savedId ? marketingAudienceSizeQ.data : option.marketingAudienceSize;
        return t('{{profiles}} profiles · {{marketing}} with marketing consent', {
            profiles: profiles?.toLocaleString() ?? '…',
            marketing: marketing?.toLocaleString() ?? '…'
        });
    };

    // A picked audience still loading counts as configured.
    const runnable = creating ? draftComplete : !!savedId && !missing;
    const state: NodeEditorState = runnable ? 'configured' : 'wontRun';

    const summary = () => {
        if (creating) {
            if (!draftComplete) {
                return t('Give the new audience a name and at least one rule.');
            }

            return marketingConsentOnly
                ? t('‘{{name}}’ is created when you save this step, and every guest in it with marketing consent goes through the steps below, one run per guest.', {name: draftName})
                : t('‘{{name}}’ is created when you save this step, and every guest in it goes through the steps below, one run per guest.', {name: draftName});
        }

        if (missing) {
            return t('‘{{name}}’ no longer exists, pick another audience.', {name: savedName || savedId});
        }

        if (hydrating) {
            return t('Loading the selected audience...');
        }

        if (!baseline?.id) {
            return t('Pick a saved audience, this step cannot run without one.');
        }

        const size = marketingConsentOnly
            ? marketingAudienceSizeQ.data
            : audienceSizeQ.data;

        const sentence = marketingConsentOnly
            ? t('Every guest in ‘{{name}}’ with marketing consent ({{profiles}} profiles) goes through the steps below, one run per guest.', {
                name: draftName || savedName,
                profiles: size?.toLocaleString() ?? '…'
            })
            : t('Every guest in ‘{{name}}’ ({{profiles}} profiles) goes through the steps below, one run per guest.', {
                name: draftName || savedName,
                profiles: size?.toLocaleString() ?? '…'
            });

        return draftChanged
            ? `${sentence} ${t('Your changes to this audience are saved with the step.')}`
            : sentence;
    };

    // The same headings as the marketing AudienceDialog.
    const formHeading = creating ? t('Create an audience') : t('Update an audience');
    const showForm = creating || (!!savedId && !missing);

    return (
        <NodeEditorLayout state={state} summary={summary()}>
            {choice === 'saved' && (
                // A stored id the list lacks is the select's unknown value, named from its snapshot.
                <SingleSelect
                    label={t('Audience')}
                    placeholder={t('Pick a saved audience')}
                    value={savedId}
                    options={options
                        .filter(option => !option.unavailable)
                        .map(option => ({value: option.id, label: option.name, detail: optionCount(option) || undefined}))}
                    onChange={id => handleSelect(options.find(option => option.id === id) ?? null)}
                    unknownLabel={savedName || undefined}
                    loading={isFetching || undefined}
                    disabled={readOnly}
                />
            )}

            {showForm && (
                hydrating && !creating
                    ? (
                        <Stack alignItems={'center'} sx={{py: 4}}>
                            <CircularProgress size={24}/>
                        </Stack>
                    )
                    : (
                        <Stack gap={1.5}>
                            <Typography variant={'subtitle2'} fontWeight={600}>
                                {formHeading}
                            </Typography>

                            <AudienceForm
                                audience={draft}
                                onChange={setDraft}
                                savedAudience={baseline}
                                readOnly={readOnly}
                            />
                        </Stack>
                    )
            )}

            <FormControlLabel
                sx={{m: 0, alignSelf: 'flex-start'}}
                control={
                    <Switch
                        size={'small'}
                        checked={marketingConsentOnly}
                        onChange={event => setNodeConfig({
                            ...nodeConfig,
                            marketingConsentOnly: event.target.checked
                        })}
                        disabled={readOnly}
                        sx={{mr: 1}}
                    />
                }
                label={t('Only guests with marketing consent')}
                slotProps={{typography: {variant: 'body2', color: 'text.secondary'}}}
            />
        </NodeEditorLayout>
    );
};

export default AudienceEditorV2;
